// Боковые панели (registry.panels): слева - «Структура» и т. п., справа - «Проверка», «Источники», «Страницы»…
// Полоса значков у края переключает панели; повторный щелчок сворачивает. Ширину панели можно менять мышью.
import { useRef, useState } from 'react';
import { registry } from '../core/registry';
import { ctx } from '../core/ctx';
import { appStore, readPref, useApp, writePref } from '../core/store';
import { useRegistry } from './hooks';
import { Icon } from './icons';
import { useUi } from './uiState';

export function PanelRail({ side }: { side: 'left' | 'right' }) {
  const panels = useRegistry(registry.panels).filter((p) => p.side === side);
  const cur = useApp((s) => s.panels[side]);
  const focus = useUi((s) => s.focusMode);
  useApp((s) => s.check);   // значки-счётчики
  useApp((s) => s.lint);
  useUi((s) => s.built);
  if (!panels.length || focus) return null;
  return (
    <div className={'rail rail-' + side} role="tablist" aria-orientation="vertical" aria-label={side === 'left' ? 'Панели слева' : 'Панели справа'}>
      {panels.map((p) => {
        let badge: number | string | null = null;
        try { badge = p.badge?.(ctx) ?? null; } catch { badge = null; }
        return (
          <button key={p.id} type="button" role="tab" aria-selected={cur === p.id} className={'rail-btn' + (cur === p.id ? ' is-active' : '')}
            data-tip={p.title} data-testid={'panel-btn-' + p.id}
            onClick={() => appStore.getState().set({ panels: { ...appStore.getState().panels, [side]: cur === p.id ? null : p.id } })}>
            <Icon name={p.icon} size={18} />
            <span className="rail-label">{p.title}</span>
            {badge ? <span className={'rail-badge tone-' + (p.badgeTone || 'neutral')}>{badge}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

export function PanelHost({ side }: { side: 'left' | 'right' }) {
  const panels = useRegistry(registry.panels);
  const cur = useApp((s) => s.panels[side]);
  const focus = useUi((s) => s.focusMode);
  const [width, setWidth] = useState<number>(() => Number(readPref('panel.w.' + side, side === 'left' ? '300' : '360')) || 320);
  const drag = useRef<{ x: number; w: number } | null>(null);
  const p = panels.find((x) => x.id === cur && x.side === side);
  if (!p || focus) return null;
  const C = p.component;
  const onDown = (e: React.PointerEvent) => {
    drag.current = { x: e.clientX, w: width };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onMove = (e: React.PointerEvent) => {
    if (!drag.current) return;
    const dx = e.clientX - drag.current.x;
    setWidth(Math.max(220, Math.min(640, drag.current.w + (side === 'left' ? dx : -dx))));
  };
  const onUp = () => { if (drag.current) { drag.current = null; writePref('panel.w.' + side, width); } };
  return (
    <aside className={'panel panel-' + side} data-testid={'panel-' + p.id} style={{ width }} aria-label={p.title}>
      <div className="panel-head">
        <span>{p.title}</span>
        <button type="button" className="panel-x" aria-label="Скрыть панель" data-tip="Скрыть панель"
          onClick={() => appStore.getState().set({ panels: { ...appStore.getState().panels, [side]: null } })}><Icon name="close" size={14} /></button>
      </div>
      <div className="panel-body"><C ctx={ctx} /></div>
      <div className={'panel-resize panel-resize-' + side} onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp}
        role="separator" aria-orientation="vertical" aria-label="Ширина панели" />
    </aside>
  );
}
